import { useTranslation } from 'react-i18next'
import type { CaseEndpoint } from '../../../types/case-container'
import type { EndpointSource } from '../../../types/collection-set'

interface SourceOptionProps {
  value: EndpointSource
  checked: boolean
  disabled: boolean
  label: string
  description: string
  onSelect: () => void
  badgeCount?: number
  badgeAccent?: boolean
}

function SourceOption(props: SourceOptionProps): React.ReactElement {
  const { value, checked, disabled, label, description, onSelect, badgeCount, badgeAccent } = props
  const labelClass = disabled
    ? 'text-slate-400 dark:text-slate-500'
    : 'text-slate-900 dark:text-slate-100'
  const badgeClass = badgeAccent
    ? 'bg-lime-100 dark:bg-lime-950/50 text-[#5c6e94] dark:text-lime-400'
    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600">
      <input
        type="radio"
        name="endpointSource"
        value={value}
        checked={checked}
        onChange={onSelect}
        disabled={disabled}
        className="h-4 w-4 text-lime-600 focus:ring-[#4e5e82]"
      />
      <div className="flex-1">
        <span className={`font-medium ${labelClass}`}>{label}</span>
        <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
      </div>
      {badgeCount !== undefined && badgeCount > 0 && (
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>
          {badgeCount}
        </span>
      )}
    </label>
  )
}

interface EndpointChecklistProps {
  itSystemEndpoints: CaseEndpoint[]
  chosenEndpointIds: Set<string>
  toggleChosenEndpoint: (id: string) => void
}

function EndpointChecklist(props: EndpointChecklistProps): React.ReactElement {
  const { itSystemEndpoints, chosenEndpointIds, toggleChosenEndpoint } = props
  return (
    <div className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
      {itSystemEndpoints.map((endpoint) => (
        <label
          key={endpoint.id}
          className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50"
        >
          <input
            type="checkbox"
            checked={chosenEndpointIds.has(endpoint.id)}
            onChange={() => {
              toggleChosenEndpoint(endpoint.id)
            }}
            className="h-4 w-4 rounded text-lime-600 focus:ring-[#4e5e82]"
          />
          <div className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {endpoint.hostname}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {endpoint.deviceType}
              {endpoint.operatingSystem ? ` \u00B7 ${endpoint.operatingSystem}` : ''}
            </span>
          </div>
        </label>
      ))}
    </div>
  )
}

export interface EndpointSourceSelectorProps {
  endpointSource: EndpointSource
  setEndpointSource: (source: EndpointSource) => void
  itSystemEndpoints: CaseEndpoint[]
  collectionSetCount: number
  chosenEndpointIds: Set<string>
  toggleChosenEndpoint: (id: string) => void
}

export function EndpointSourceSelector(props: EndpointSourceSelectorProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { endpointSource, setEndpointSource, itSystemEndpoints } = props
  const { collectionSetCount, chosenEndpointIds, toggleChosenEndpoint } = props
  const collectionDescription =
    collectionSetCount > 0
      ? t('collections.endpointsTagged', { count: collectionSetCount })
      : t('collections.noEndpointsInSet')
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('collections.targetEndpoints')}
      </label>
      <div className="space-y-2">
        <SourceOption
          value="collection_set"
          checked={endpointSource === 'collection_set'}
          disabled={collectionSetCount === 0}
          label={t('collections.collectionSet')}
          description={collectionDescription}
          onSelect={() => {
            setEndpointSource('collection_set')
          }}
          {...(collectionSetCount > 0 ? { badgeCount: collectionSetCount, badgeAccent: true } : {})}
        />
        <SourceOption
          value="all"
          checked={endpointSource === 'all'}
          disabled={itSystemEndpoints.length === 0}
          label={t('collections.allItSystems')}
          description={t('collections.allItSystemsDesc')}
          onSelect={() => {
            setEndpointSource('all')
          }}
          badgeCount={itSystemEndpoints.length}
        />
        <SourceOption
          value="choose"
          checked={endpointSource === 'choose'}
          disabled={itSystemEndpoints.length === 0}
          label={t('collections.chooseEndpoints')}
          description={t('collections.chooseEndpointsDesc')}
          onSelect={() => {
            setEndpointSource('choose')
          }}
          {...(chosenEndpointIds.size > 0
            ? { badgeCount: chosenEndpointIds.size, badgeAccent: true }
            : {})}
        />
        {endpointSource === 'choose' && (
          <EndpointChecklist
            itSystemEndpoints={itSystemEndpoints}
            chosenEndpointIds={chosenEndpointIds}
            toggleChosenEndpoint={toggleChosenEndpoint}
          />
        )}
      </div>
    </div>
  )
}
